import { ApiError, ForbiddenError, type intelligence } from '@cwos/api-client';

/**
 * Pure view logic for Ask Phibian — everything here is a function of its
 * arguments so the honesty rules can be tested without a DOM.
 *
 * The screen renders what these decide; it never re-derives any of it inline,
 * because a second spelling of "what could not be verified" is a second
 * spelling that can drift into politeness.
 */

/**
 * The 503 the turn route answers when no LLM provider is configured
 * (routes.ts: code `intelligence_provider_unconfigured`, sent both when the
 * provider is absent at startup and when its credential is missing at call
 * time). This is a fact about the DEPLOYMENT, not a failure of the request —
 * the screen renders it as an honest not-configured state with no retry
 * button, because asking again cannot configure a provider.
 *
 * The code is required, not just the status: a 503 from a proxy or an
 * overloaded API is a transient this predicate must not capture.
 */
export function isProviderUnconfigured(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 503 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    (err.body as { code?: unknown }).code === 'intelligence_provider_unconfigured'
  );
}

/**
 * The 409 the turn route answers when a presented confirmation is not live:
 * already used, expired, issued to someone else, or naming a different plan
 * (routes.ts: code `confirmation_not_live`). Rendered as its own sentence —
 * not a generic failure — because the fix is to ask again for a fresh
 * proposal, not to retry the same one.
 */
export function isConfirmationNotLive(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    (err.body as { code?: unknown }).code === 'confirmation_not_live'
  );
}

/**
 * The 403 the turn route answers when the DEPLOYMENT's data-classification
 * policy forbids sending this organization's records to a model
 * (routes.ts: code `intelligence_transmission_denied`, sent from both the
 * pre-flight verdict and the `TransmissionDeniedError` catch — in both cases
 * BEFORE any provider call).
 *
 * This is not the same fact as {@link isWithheld} and must never be rendered
 * as one. `intelligence:use` is a grant an org admin can make; a transmission
 * denial is a classification decision no permission grant can lift. Telling
 * the reader to "ask your org admin" sends them to somebody who cannot fix it,
 * and then hides the composer for a reason they were told wrongly.
 *
 * The code is required, not just the status, for the same reason the two
 * predicates above require theirs.
 */
export function isTransmissionDenied(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    (err.body as { code?: unknown }).code === 'intelligence_transmission_denied'
  );
}

/**
 * 403 — `intelligence:use` is not held (or was revoked mid-session).
 *
 * NARROWED, and the narrowing is the point. This used to be
 * `err instanceof ForbiddenError` with no code check, so the ONE 403 that is
 * not about the caller's permissions — `intelligence_transmission_denied` —
 * took this branch and was rendered as a missing key. Two different refusals
 * with two different remedies were collapsed into the one whose remedy does
 * not work. See {@link isTransmissionDenied}, which is checked first at the
 * call site and excluded here so the two can never both be true.
 */
export function isWithheld(err: unknown): boolean {
  return err instanceof ForbiddenError && !isTransmissionDenied(err);
}

/**
 * The caution a non-grounded verdict earns, or null for `grounded` (which is
 * silent — an audit that passed is not a banner).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THESE SENTENCES NO LONGER NAME REFS.
 *
 * The audit's refs are manifest entry ids in `toolId@n` notation, and this
 * caution used to print them: "Could not verify: analytics.query_metric@2."
 * The intent was accountability — naming what could not be verified so the
 * claim is checkable rather than vague. The effect on a reader who does not
 * know what a manifest entry is was the opposite: a string of runtime
 * vocabulary that reads as breakage, attached to the one message that most
 * needs to be understood.
 *
 * So the caution now says the SAME FACT in the reader's terms — which parts
 * of the answer stand on records and which do not — and drops the notation.
 * Nothing is softened: an ungrounded answer is still called unverified, in the
 * same place, at the same volume. What changes is that the sentence is aimed
 * at the person who has to decide whether to trust it.
 *
 * The refs themselves are NOT lost. They ride every turn response in
 * `grounding.supported` / `unsupported` / `orphanRefs`, unchanged, and the
 * server records them in its own audit. A developer investigating a specific
 * verdict reads the response; a user reads the sentence.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE RULES, stated once:
 *   - `ungrounded` with unsupported refs — the answer's citations resolve to
 *     nothing that was actually retrieved: say it is unverified.
 *   - `ungrounded` with no refs — the answer cites no evidence at all
 *     (grounding.ts: an answer that cites nothing cannot be certified
 *     grounded): say that, which is a different sentence from "fabricated".
 *   - `partially_grounded` — some citations held, some did not.
 *   - `uncited` — small talk, a clarifying question: visible, calm.
 *
 * Every branch ends by saying the answer is Phibian's own, unedited. The
 * audit's whole point is exposure; a caution that quietly justified trimming
 * the answer would defeat it.
 *
 * THE CLAUSE USED TO SAY "nothing has been removed", AND THAT STOPPED BEING
 * TRUE (L182). Citation tokens are now cut out of the answer before anybody
 * sees it — `redactCitations` on the server, `readableAnswer` below as the
 * render-side guarantee. A promise a reader could check and find false is
 * worse than no promise, so the clause now claims the thing that is still
 * exactly true and is the thing it was always really about: not one word of
 * Phibian's writing has been reworded, softened or cut. What was removed was
 * a machine reference, which was never writing.
 */
export interface GroundingCaution {
  text: string;
  /**
   * 'alert' is announced (role="alert") — citations that resolve to nothing
   * are an integrity event. 'note' renders visibly but calmly (F186):
   * a citation-free answer — small talk, a clarifying question — is a
   * different fact from fabrication, and shouting it on every greeting
   * would teach users to ignore the voice that matters.
   */
  tone: 'alert' | 'note';
}

export function groundingCaution(grounding: intelligence.GroundingAudit): GroundingCaution | null {
  if (grounding.verdict === 'grounded') return null;
  if (grounding.verdict === 'uncited') {
    return {
      tone: 'note',
      text: 'This answer isn’t based on your organization’s records — nothing in it was checked against them. Phibian’s own wording is shown, unedited.',
    };
  }
  if (grounding.verdict === 'ungrounded') {
    return {
      tone: 'alert',
      text:
        grounding.unsupported.length > 0
          ? 'Phibian couldn’t match this answer to your organization’s records, so treat it as unverified. Phibian’s own wording is shown, unedited.'
          : 'This answer isn’t backed by anything from your organization’s records, so nothing in it has been verified. Phibian’s own wording is shown, unedited.',
    };
  }
  return {
    tone: 'alert',
    text: 'Parts of this answer couldn’t be matched to your organization’s records. The rest is backed by what Phibian looked at. Phibian’s own wording is shown, unedited.',
  };
}

/** Back-compat shim for callers/tests that only need the words. */
export function groundingCautionText(grounding: intelligence.GroundingAudit): string | null {
  return groundingCaution(grounding)?.text ?? null;
}

/**
 * What an answer could NOT reach, said once and without narrating steps.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE THING THIS PRESERVES FROM THE DELETED TRAIL.
 *
 * The per-step activity trail was removed because a finished answer should
 * show a result, not a trace. But one property of that trail was not
 * decoration: a retrieval the caller was REFUSED changes what the answer is
 * worth, and only the reader can judge that. Dropping the trail without
 * replacing that signal would have quietly turned "I could only see part of
 * this" into "here is the answer".
 *
 * So the signal survives, aggregated into at most one sentence, derived from
 * `summary.toolOutcomes` — the server's own record of what ran. It names no
 * tool, no step, no order and no count. It says the two things a reader needs:
 * that the answer is built on less than the question asked for, and what — if
 * anything — they can do about it.
 *
 * THE THIRD BRANCH, AND THE ADVICE THAT WAS WRONG. There used to be two arms:
 * `denied` ("ask your org admin") and everything-else ("asking again may get
 * further"). `unsupported` fell into the second, and `unsupported` is what the
 * analytics executor raises for a figure it will not publish because too few
 * people stand behind it (`withheld()` in `analytics/engineExecutor.ts`, code
 * `subjects_below_threshold`). So a privacy floor was described to the reader
 * as a transient fault, and they were invited to retry a request that cannot
 * ever succeed — a suggestion that is not merely useless but, repeated across
 * narrowing windows, is the shape of an attempt to walk around the floor.
 *
 * `unsupported` also covers the measures the product simply does not offer that
 * way, and "asking again will not change this" is true of those too, so the
 * branch was honest for its whole membership rather than for the one case that
 * prompted it. Honest, but coarse — it is the state F409 then split, below.
 *
 * THE FOURTH BRANCH, AND WHAT USED TO BE MISSING (F409). This block used to
 * end here, saying that the server drew the distinction the reader wanted —
 * `tools/refusalCategory.ts` files `subjects_below_threshold` as
 * `small_group_protected` and NOT as `no_access` — and that the category did
 * not reach the client, because `toolOutcomes[]` carried `outcome` plus an
 * `errorKind` that was a copy of it. Naming the floor from this side would have
 * meant guessing, and a guess about which of two things happened is exactly the
 * substitution this file exists to refuse.
 *
 * The category now crosses the wire, so the guess is gone and the sentence is
 * the server's ruling rather than this file's inference. A cohort below the
 * disclosure floor is a PRIVACY PROTECTION WORKING CORRECTLY, and what a reader
 * needs to know about it is the opposite of what they need for the other three:
 * not "ask an admin" (nobody has anything to grant), not "ask differently" (a
 * narrower question makes the cohort SMALLER, which is worse and is the shape
 * of walking around the floor), but that the figure exists, is theirs to see in
 * principle, and is being held back to keep a person out of a report.
 *
 * WHAT IT DOES NOT SAY, and must never grow into: how many people stand behind
 * the figure, or which figure it was. The category carries neither, and this
 * sentence names the floor rather than a count.
 *
 * PRECEDENCE IS DELIBERATE, AND `denied` STILL WINS. One `subjects_below_
 * threshold` refusal is raised with `outcome: 'denied'` — counting individual
 * people needs operational access this account does not hold — and for THAT one
 * an admin genuinely does have something to grant. So the floor sentence is
 * reached only from `unsupported`, which is where `withheld()` lands, and a
 * denial keeps the advice that has a next step. Permission first, then the
 * floor, then the unoffered, then the transient: the order is "does the reader
 * have something to do about it", most actionable first.
 */
export function answerLimits(summary: intelligence.TurnSummary): string | null {
  let denied = false;
  let smallGroup = false;
  let unsupported = false;
  let failed = false;
  for (const outcome of summary.toolOutcomes) {
    if (outcome.outcome === 'ok') continue;
    if (outcome.outcome === 'denied') denied = true;
    else if (outcome.outcome === 'unsupported') {
      // The category REFINES this arm; it does not replace it. An `unsupported`
      // carrying no category (an older server, or a refusal reason nobody has
      // filed) still reads as unoffered, which is what it did before this
      // existed — a missing category must not silently become a privacy claim.
      if (outcome.refusalCategory === 'small_group_protected') smallGroup = true;
      else unsupported = true;
    } else failed = true;
  }
  if (denied) {
    return 'Some of the records this answer needed aren’t open to your account, so it may be incomplete. Ask your org admin if you need broader access.';
  }
  if (smallGroup) {
    return 'Part of this was held back because too few people stand behind it to report without identifying them, so this answer may be incomplete. That is a privacy protection working, not a permission problem: nobody can grant it away, and a narrower question will not lift it either.';
  }
  if (unsupported) {
    return 'Part of this question could not be answered as asked, so this answer may be incomplete. Asking again the same way will not change that — a different question may.';
  }
  if (failed) {
    return 'Some records couldn’t be read just now, so this answer may be incomplete. Asking again may get further.';
  }
  return null;
}

/**
 * The one turn ending that is not a clean stop, said plainly.
 *
 * `round_limit` means the tool loop hit its bound with work left, so the
 * answer on screen may be built on less than the question needed. Reported by
 * the server in `summary.status` — this states it, it does not infer it.
 *
 * Deliberately free of runtime vocabulary: the old sentence said Phibian
 * "reached the limit on how many steps one turn may take", which explains the
 * tool loop's round bound to somebody who has never heard of it. What a reader
 * needs is that the answer is partial and that a narrower question gets
 * further.
 *
 * THERE WAS A SECOND SENTENCE HERE, AND NOBODY COULD EVER HAVE READ IT (F297).
 * `summary.status` used to admit `'error'`, and this function had a branch for
 * it — "Something went wrong partway through…". A turn that fails answers
 * 502 / 503 / 409 with an error body and no summary, so the branch was
 * unreachable by construction: a sentence written for a person who could not
 * be shown it, in code no test could enter, that read as covered because the
 * type said the case existed. The failure the reader DOES see is the composer's
 * error line, which is reached from the ApiError — `isConfirmationNotLive` and
 * friends above — not from a summary that never arrives.
 *
 * The `switch` is exhaustive on purpose. If `turnSummarySchema` ever grows a
 * third ending, the `never` assignment stops the build HERE, so the question
 * "what do we tell the person about this one?" gets answered by somebody
 * rather than defaulted to silence.
 */
export function turnEndingNote(summary: intelligence.TurnSummary): string | null {
  switch (summary.status) {
    case 'completed':
      return null;
    case 'round_limit':
      return 'This question took more work than Phibian can do in one go, so it answered with what it had. A narrower question will get further.';
    default: {
      const unhandled: never = summary.status;
      return unhandled;
    }
  }
}

// ---------------------------------------------------------------------------
// The answer as prose (L182)
// ---------------------------------------------------------------------------

/**
 * One citation token, in either notation the runtime has ever used: a
 * server-issued `ev_` reference, or the old derived `toolId@n` shape.
 *
 * Both are correlation material. Neither has ever been readable by, useful to,
 * or lookup-able by the person holding it.
 */
const CITATION_TOKEN_SOURCE = '(?:ev_[0-9a-f]{32}|[a-z][a-z0-9_]*(?:\\.[a-z][a-z0-9_]*)+@\\d+)';

/** A trailing "Evidence: …" block — the line the model is asked to append. */
const TRAILING_CITATION_BLOCK = /(?:^|\n)[ \t]*evidence[ \t]*:[\s\S]*$/i;

/**
 * A bracket group whose ENTIRE contents are citations — `(ev_…)` — cut as one
 * unit, together with the space in front of it.
 *
 * Deliberately NOT "remove the tokens, then delete any empty bracket". An
 * answer can legitimately contain a bracket that was already empty (a model
 * quoting `[]` back out of something it was shown), and a general sweep would
 * eat it. This only ever deletes a bracket it can see a citation inside.
 */
const BRACKETED_CITATIONS = new RegExp(
  `[ \\t]*[([{][\\s,;:·]*${CITATION_TOKEN_SOURCE}(?:[\\s,;:·]+${CITATION_TOKEN_SOURCE})*[\\s,;:·]*[)\\]}]`,
  'gi',
);

/** A citation token standing on its own in prose. */
const BARE_CITATION = new RegExp(CITATION_TOKEN_SOURCE, 'gi');

/**
 * The answer, as prose a person can read — or null when the turn produced no
 * prose at all.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE SERVER ALREADY DOES THIS, AND THAT IS NOT A REASON TO SKIP IT HERE.
 *
 * `POST /api/intelligence/turn` redacts citations before it sends `answer`
 * (routes.ts `redactCitations`), and that is where the authority lives: it is
 * the only place holding the RAW text, it runs the grounding audit on that raw
 * text first, and it is what keeps the stored conversation clean for every
 * client that will ever read it. Nothing here weakens any of that.
 *
 * This exists because the leak the owner photographed was a RENDER, and a
 * render is the last place it can be stopped. The screen is handed a string it
 * did not produce, across a version boundary it does not control — an older
 * API revision behind a split traffic allocation, a thread row written before
 * the redactor shipped, some future field that carries model prose for another
 * reason entirely. "The server promised" is not a thing a component can check,
 * and `ev_bd38e…` in a chat bubble is not a defect worth being right about.
 *
 * IT IS DELIBERATELY NARROWER THAN THE SERVER'S. It cuts citation SHAPES and
 * the citation LINE, and nothing else. It does not reword, summarize, shorten
 * or touch a single claim — this surface's whole doctrine is that an answer is
 * shown as written, and the one thing removed here was never writing. An
 * answer with no citation in it is returned byte-for-byte, by the early return
 * below: the tidy-up repairs the seam a cut leaves and has no business running
 * on text nothing was cut from.
 *
 * Returns null rather than '' so the caller has to decide what an empty answer
 * looks like, instead of rendering an empty bubble by accident.
 */
export function readableAnswer(answer: string): string | null {
  if (typeof answer !== 'string' || answer.trim() === '') return null;
  const cut = answer
    .replace(TRAILING_CITATION_BLOCK, '')
    .replace(BRACKETED_CITATIONS, '')
    .replace(BARE_CITATION, '');
  if (cut === answer) return answer;
  const prose = cut
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+([.,;:!?])/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return prose.length > 0 ? prose : null;
}

/**
 * What to show when a turn came back with no prose.
 *
 * Rare and real: a completion that produced only a citation line, or only
 * whitespace. This surface used to render that as the empty string, which is a
 * blank bubble under the word "Phibian" — indistinguishable from a rendering
 * bug and impossible to act on. This says the true thing in one sentence and
 * names the one move that might work, which is the standard every other honest
 * state on this screen is held to.
 */
export const NO_PROSE_ANSWER =
  'Phibian didn’t put an answer into words this time. Asking again, or asking more narrowly, usually gets one.';
